/**
 * TaskStageDiff — a task Status value that a proposal changes, rendered as
 * `current → proposed`.
 *
 * The task counterpart of `AssociationDiffValue`'s swap shape: exactly one
 * stage goes and one arrives (a record holds one stage per pipeline), so the
 * arrow form is always honest here. Both sides render through the canonical
 * `TaskStageLink` pill; the current side is de-emphasised so the proposed
 * stage reads as the destination, matching how the record overview renders a
 * projected change (`old → new`) rather than silently swapping the value.
 *
 * Used by the Status row in `TaskFieldRows` wherever a proposed stage is in
 * play: the task slideout under a pending proposal, and the task
 * stage-update action card.
 */

import { ArrowRight } from '@untitledui/icons';

import { TaskStageLink } from './TaskStage';

export interface TaskStageDiffProps {
  orgId: number;
  /** The stage the task is in now; null when it has none. */
  currentStageEntityId: string | null;
  /** The stage the proposal moves it to; null renders as a removal. */
  proposedStageEntityId: string | null;
}

export function TaskStageDiff({
  orgId,
  currentStageEntityId,
  proposedStageEntityId,
}: TaskStageDiffProps) {
  return (
    <span className='flex min-w-0 flex-wrap items-center gap-1.5'>
      {currentStageEntityId && (
        <>
          <span className='opacity-60'>
            <TaskStageLink orgId={orgId} stageEntityId={currentStageEntityId} />
          </span>
          <ArrowRight
            className='size-3 shrink-0 text-fg-quaternary'
            aria-hidden='true'
          />
        </>
      )}
      {proposedStageEntityId ? (
        <TaskStageLink orgId={orgId} stageEntityId={proposedStageEntityId} />
      ) : (
        <span className='text-sm text-[var(--color-text-tertiary)]'>
          (removed)
        </span>
      )}
    </span>
  );
}
